/* eslint-disable @nx/enforce-module-boundaries */
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BaseListItems,
  CardTableComponent,
  ConfirmDialogService,
  FilterParams,
  LoaderIndicatorDirective,
} from '@t-llevo/common-ui';
import { ResponseData } from '@t-llevo/util';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { map, take, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TravelPricingFormComponent } from '../../travel-pricing-form/travel-pricing-form.component';
import { MatButtonModule } from '@angular/material/button';
import { SelectFilterComponent } from 'admin-drivers/src/app/common/components/select-filter/select-filter.component';
import { TagComponent } from 'admin-drivers/src/app/common/components/tag.component';
import { TravelPricingResponse } from 'admin-drivers/src/app/api/models/responses/TravelPricingResponse';
import { apiTravelPricingRoutes } from 'admin-drivers/src/app/api/routes/api-travel-pricing-routes';
import { TravelPricingService } from 'admin-drivers/src/app/api/services/travel-pricing.service';
import { VehicleTypesService } from 'admin-drivers/src/app/api/services/vehicle-types.service';
import { TravelPricing } from 'admin-drivers/src/app/common/models/TravelPrincing';
import { Currency } from '../../../const/currency';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TravelPricingDetailsComponent } from '../../travel-pricing-details/travel-pricing-details.component';

@Component({
  selector: 't-llevo-travel-pricing-list',
  standalone: true,
  imports: [
    CommonModule,
    CardTableComponent,
    SelectFilterComponent,
    MatTableModule,
    LoaderIndicatorDirective,
    TagComponent,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatTooltipModule,
    MatButtonModule,
    TravelPricingDetailsComponent,
  ],
  providers: [ConfirmDialogService],
  templateUrl: './travel-pricing-list.component.html',
})
export class TravelPricingListComponent
  extends BaseListItems<TravelPricing, ResponseData<TravelPricingResponse>>
  implements OnDestroy, AfterViewInit
{
  displayedColumns: string[] = [
    'vehicleType',
    'currency',
    'baseFee',
    'status',
    'actions',
  ];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('currencyFilter') currencyFilter!: SelectFilterComponent;
  @ViewChild('vehicleTypeFilter') vehicleTypeFilter!: SelectFilterComponent;

  showDrawer = false;
  sortDirection: SortDirection = 'asc';
  currencyList = Currency.map((currency) => {
    return {
      id: currency,
      value: currency,
    };
  });
  destroyRef = inject(DestroyRef);

  protected vehicleTypeSrv = inject(VehicleTypesService);
  VehicleTypesNames = toSignal(
    this.vehicleTypeSrv.getVehicleTypes().pipe(
      map((data) => {
        const availableVehicles = data.filter((vehicle) => vehicle.available);
        return availableVehicles.map((item) => {
          return {
            id: item.id,
            value: item.name,
          };
        });
      })
    )
  );

  constructor(
    public dialog: MatDialog,
    private pricingSrv: TravelPricingService
  ) {
    super(pricingSrv, apiTravelPricingRoutes.routePricing);
    this.vehicleTypeSrv.getVehicleTypes().pipe(take(1)).subscribe();
  }

  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.loadItems(this._getFiltersParams()))
      )
      .subscribe();
    this.loadItems(this._getFiltersParams());
  }

  _getFiltersParams(defaultField = 'name'): FilterParams {
    const params: FilterParams = {
      page: this.paginator ? this.paginator.pageIndex + 1 : 1,
      limit: this.paginator ? this.paginator.pageSize : 10,
    };
    if (this.vehicleTypeFilter.selectedItems.length > 0) {
      params['typeVehicle'] = this.vehicleTypeFilter.selectedItems.toString();
    }
    if (this.currencyFilter.selectedItems.length > 0) {
      params['currency'] = this.currencyFilter.selectedItems.toString();
    }

    return params;
  }

  detail(item: TravelPricing): void {
    this.showDrawer = !this.showDrawer;
    this.selectItem(item.id);
  }

  openedDrawerChange(open: boolean) {
    this.showDrawer = open;
  }

  private selectItem(id: number | string) {
    this.pricingSrv.getItem('', id, false);
  }

  openAddItemDialog() {
    this.dialog
      .open(TravelPricingFormComponent, {
        width: '90%',
        maxHeight: '90%',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems(this._getFiltersParams());
        }
      });
  }

  openEditDialog(itemData: TravelPricing) {
    this.dialog
      .open(TravelPricingFormComponent, {
        data: {
          edit: true,
          data: itemData,
        },
        width: '90%',
        maxHeight: '90%',
        disableClose: true,
        panelClass: 'bg-gray',
      })
      .afterClosed()
      .subscribe((data: boolean) => {
        if (data) {
          this.loadItems(this._getFiltersParams());
        }
      });
  }

  openChangeStatusDialog(pricing: TravelPricing) {
    const config = {
      title: `¿Desea ${
        pricing.isActive ? 'deshabilitar' : 'habilitar'
      } el precio?`,
      message: pricing.isActive
        ? `¿Desea deshabilitar el precio seleccionado? No se aplicará este precio a las nuevas solicitudes de viajes`
        : `¿Desea habilitar el precio seleccionado? Se aplicará este precio a las nuevas solicitudes de viajes`,
      cancelText: 'CANCELAR',
      confirmText: `${pricing.isActive ? 'DESHABILITAR' : 'HABILITAR'}`,
    };
    this.confirmDialogSrv.open(config).subscribe((dataDialog) => {
      if (dataDialog) {
        this.pricingSrv.changePricingActiveStatusById(pricing.id).subscribe({
          next: () => {
            this.notifySrv.dispatch(
              `Precio ${pricing.isActive ? 'deshabilitado' : 'habilitado'}`,
              'success'
            );
            this.loadItems(this._getFiltersParams());
          },
        });
      }
    });
  }

  openDeleteDialog(item: TravelPricing) {
    const config = {
      title: `¿Desea eliminar el elemento?`,
      message: 'El elemento se eliminará de forma permanente',
      cancelText: 'CANCELAR',
      confirmText: 'SI, DESEO ELIMINAR',
    };
    this.confirmDialogSrv.open(config).subscribe((dataDialog) => {
      if (dataDialog) {
        this.pricingSrv.deletePriceById(item.id).subscribe({
          next: () => {
            this.notifySrv.dispatch('Precio eliminado', 'success');
            this.loadItems(this._getFiltersParams());
          },
        });
      }
    });
  }

  filter() {
    this.loadItems(this._getFiltersParams());
  }

  getVehicleById(id: string) {
    return this.VehicleTypesNames()?.find((elem) => elem.id === id)?.value;
  }
}
